/**
 * `/about-us/news-events/news/`: the Ryerson All News page, every news article with category
 * tabs (`components/NewsEvents/News/AllNews.tsx`). On any other `NEXT_PUBLIC_COMPANY` the page
 * isn't built.
 *
 * Copy comes from `src/content/ryerson/about-us/news-events/news/index.ts`, whose `PageContent` is
 * the page's site-search entry. The article list is fetched at build time by
 * `src/contentful/news/getContentfulNewsForSections.ts`; each card links to
 * `./[categorySlug]/[titleSlug].tsx`. Pinned by `src/unit-test/AboutUs/News.spec.tsx` (a render
 * smoke test with empty lists).
 */
import React from "react";
import { useApplication } from "@ryerson/frontend.application";
import AllNews from "@components/NewsEvents/News/AllNews";
import MetaHead from "@components/Shared/model/MetaHead";
import { useHasMounted } from "@components/Hooks";
import { StaticContent, MetaObject } from "@content/ryerson/about-us/news-events/news";
import { allContentfulHeaderCalls, HeaderPageProps } from "@ryerson/frontend.contentful";
import HeaderLayout from "@components/NextLayouts/HeaderLayout";
import { getHeaderData } from "@components/NextLayouts/nextUtilities";
import { getContentfulNewsForSections } from "@contentful/news/getContentfulNewsForSections";
import { ProcessedContentfulNewsForSectionsByLanguage } from "@contentful/news/types";

/**
 * Ryerson build only. Fetches the header and footer data and the news list at build time; a
 * Contentful failure fails the build.
 */
export const getStaticProps = async () => {
	if (process.env.NEXT_PUBLIC_COMPANY !== "ryerson") {
		return { notFound: true }; // tells Next.js to skip page generation and return 404
	}

	const { props: headerFooterProps } = await allContentfulHeaderCalls();

	return {
		props: {
			...headerFooterProps,
			contentfulNewsByLanguage: await getContentfulNewsForSections(),
		},
	};
};

/** Renders the All News page in the current language, after the first client mount. */
const Page: React.FC<
	HeaderPageProps & {
		contentfulNewsByLanguage: ProcessedContentfulNewsForSectionsByLanguage;
	}
> = (props) => {
	const {
		localization: { language },
	} = useApplication();

	if (!useHasMounted()) return null;

	return (
		<HeaderLayout {...getHeaderData(props)}>
			<MetaHead content={MetaObject[language]} />
			<AllNews
				content={StaticContent[language]}
				contentfulNewsByLanguage={props.contentfulNewsByLanguage}
			/>
		</HeaderLayout>
	);
};

export default Page;
